import React from 'react';
import { useFlowConfigHistory } from './useFlowConfigHistory';
import { useFlowConfig } from './useFlowConfig';
import { Checkbox } from 'components/ui/checkbox';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import firebase from 'firebase/compat/app';
import { firestore } from '../../../firebase';
import { Bell, CheckCircle2, Loader2 } from 'lucide-react';
import { cn } from 'lib/utils';

interface GenericAlarmListProps {
  deviceId: string;
}

// Resolve alarm using Firebase SDK directly
const resolveAlarm = async ({
  configId,
  historyId,
}: {
  configId: string;
  historyId: string;
}): Promise<void> => {
  const userId = firebase.auth().currentUser?.uid;

  await firestore
    .collection('flowDeviceConfigs')
    .doc(configId)
    .collection('history')
    .doc(historyId)
    .update({
      resolved: true,
      resolvedAt: firebase.firestore.FieldValue.serverTimestamp(),
      resolvedBy: userId,
    });
};

export default function GenericAlarmList({ deviceId }: GenericAlarmListProps) {
  const queryClient = useQueryClient();
  const { data: flowConfig, isLoading: configLoading } = useFlowConfig(deviceId);
  const { history, isLoading: historyLoading } = useFlowConfigHistory(flowConfig?.id || null);

  const configId = flowConfig?.id;

  const resolveMutation = useMutation({
    mutationFn: resolveAlarm,
    onSuccess: () => {
      // Invalidate the history query to refetch
      queryClient.invalidateQueries({
        queryKey: ['flowDeviceConfigHistory', configId],
      });
    },
  });

  // Filter only genericAlarm types (both resolved and unresolved)
  const genericAlarms = history.filter((item) => item.type === 'genericAlarm');
  const unresolvedCount = genericAlarms.filter((item) => !item.resolved).length;

  const isLoading = configLoading || historyLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4 rounded-lg border bg-white">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!configId) {
    return null;
  }

  if (genericAlarms.length === 0) {
    return (
      <div className="rounded-lg border border-green-100 bg-green-50 p-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          <span className="text-sm font-medium text-green-700">Ingen alarmer</span>
        </div>
      </div>
    );
  }

  const handleResolve = (historyId: string) => {
    if (configId) {
      resolveMutation.mutate({ configId, historyId });
    }
  };

  return (
    <div className="rounded-lg border bg-white">
      <div className="flex items-center gap-2 p-4 border-b bg-gray-50 rounded-t-lg">
        <Bell
          className={cn('w-4 h-4', unresolvedCount > 0 ? 'text-orange-500' : 'text-green-500')}
        />
        <span className="text-sm font-medium text-gray-700">
          Alarmer ({unresolvedCount} uløste af {genericAlarms.length})
        </span>
      </div>
      <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
        {genericAlarms.map((item) => {
          const isResolved = item.resolved;
          const isPending =
            resolveMutation.isPending && resolveMutation.variables?.historyId === item.id;

          return (
            <li
              key={item.id}
              className={cn(
                'flex items-start gap-3 p-4 transition-colors',
                !isResolved && 'hover:bg-gray-50',
                isResolved && 'bg-gray-50/50',
                isPending && 'opacity-50'
              )}
            >
              <Checkbox
                id={`alarm-${item.id}`}
                checked={isResolved}
                disabled={isResolved || resolveMutation.isPending}
                onCheckedChange={() => handleResolve(item.id)}
                className="mt-0.5"
              />
              <label
                htmlFor={`alarm-${item.id}`}
                className={cn('flex-1', !isResolved && 'cursor-pointer')}
              >
                <div
                  className={cn(
                    'text-sm',
                    isResolved ? 'text-gray-400 line-through' : 'text-gray-700'
                  )}
                >
                  {item?.context?.alerts?.join(', ') || 'Generisk alarm'}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {item.createTime.toLocaleString('da-DK')}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
